import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import PlatformSelector from "./components/PlatformSelector";
import PlaylistGrid from "./components/PlaylistGrid";
import StepIndicator from "./components/StepIndicator";
import SyncSummary from "./components/SyncSummary";
import { useGlobalAuth } from "../../contexts/GlobalContext";
import { usePreparePlaylists } from "../../hooks/usePreparePlaylists";

const PlaylistSelectionSyncSetup = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedPlaylists, setSelectedPlaylists] = useState([]);
  const [destinationPlatform, setDestinationPlatform] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { spotify, youtube } = useGlobalAuth();

  const { isLoggedIn, spotifyPlaylist } = spotify;
  const { googleToken, youtubePlaylists } = youtube;

  const { enhancePlaylistsWithTracks } = usePreparePlaylists();
  const [playlistsWithTracks, setPlaylistsWithTracks] = useState([]);

  const enhancePlaylists = async () => {
    const playlistsWithTracks = await enhancePlaylistsWithTracks(
      // selectedPlatform==="youtube" ? youtubePlaylists : spotifyPlaylist,
      playlists[selectedPlatform],
      selectedPlatform
    );
    setPlaylistsWithTracks(playlistsWithTracks);
  };

  useEffect(() => {
    if (selectedPlatform) {
      enhancePlaylists();
    }
  }, [selectedPlatform]);

  // Map real Spotify playlists to UI format
  const mappedSpotifyPlaylists = spotifyPlaylist.map((p) => ({
    id: p.id,
    name: p.name,
    description: p.description,
    coverUrl: p.images && p.images[0] ? p.images[0].url : "",
    trackCount: p.tracks.total,
    public: p.public,
    owner: p.owner?.display_name || p.owner?.id || "",
    // Add any other fields your card needs
  }));

  // Map real YouTube playlists to UI format
  const mappedYouTubePlaylists = youtubePlaylists.map((p) => ({
    id: p.id,
    name: p.snippet?.title || "",
    description: p.snippet?.description || "",
    coverUrl:
      p.snippet?.thumbnails?.medium?.url ||
      p.snippet?.thumbnails?.default?.url ||
      "",
    trackCount: p.contentDetails?.itemCount || 0,
    public: p.status ? p.status.privacyStatus === "public" : true,
    owner: p.snippet?.channelTitle || "",
    // Add any other fields your card needs
  }));

  const [playlists, setPlaylists] = useState({
    spotify: mappedSpotifyPlaylists,
    youtube: mappedYouTubePlaylists,
  });

  // Update playlists when real Spotify or YouTube playlists change
  useEffect(() => {
    setPlaylists((prev) => ({
      ...prev,
      spotify: mappedSpotifyPlaylists,
      youtube: mappedYouTubePlaylists,
    }));
  }, [spotifyPlaylist, youtubePlaylists]);

  // Mock connected accounts data
  const [connectedAccounts, setConnectedAccounts] = useState({
    spotify: isLoggedIn,
    youtube: !!googleToken,
  });

  useEffect(() => {
    setConnectedAccounts({ spotify: isLoggedIn, youtube: !!googleToken });
  }, [googleToken, isLoggedIn]);

  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
    setDestinationPlatform(platform === "spotify" ? "youtube" : "spotify");
    setCurrentStep(2);
  };

  const handlePlaylistToggle = (playlistId) => {
    setSelectedPlaylists((prev) => {
      if (prev.includes(playlistId)) {
        return prev.filter((id) => id !== playlistId);
      } else {
        return [...prev, playlistId];
      }
    });
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      const allPlaylistIds = playlists[selectedPlatform].map((p) => p.id);
      setSelectedPlaylists(allPlaylistIds);
    } else {
      setSelectedPlaylists([]);
    }
  };

  const handleContinueToSummary = () => {
    if (selectedPlaylists.length === 0) {
      return;
    }
    setCurrentStep(3);
  };

  const handleBackToSelection = () => {
    setCurrentStep(2);
  };

  const getSelectedPlaylistsData = () => {
    return playlists[selectedPlatform].filter((p) =>
      selectedPlaylists.includes(p.id)
    );
  };

  const canContinue = () => {
    switch (currentStep) {
      case 1:
        return selectedPlatform !== null;
      case 2:
        return selectedPlaylists.length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleContinueToPreview = () => {
    setIsLoading(true);
    
    // Simulate navigation delay
    setTimeout(() => {
      navigate('/sync-preview-configuration', {
        state: {
          sourcePlatform: selectedPlatform,
          destinationPlatform: destinationPlatform,
          selectedPlaylists: getSelectedPlaylistsData(),
          totalTracks: getSelectedPlaylistsData().reduce((sum, p) => sum + p.trackCount, 0)
        }
      });
    }, 1000);
  };
  

  return (
    <div className="bg-background">
      <div className="pt-8  pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-text-primary mb-2">
              Playlist Selection & Sync Setup
            </h2>
            <p className="text-text-secondary">
              Choose your source playlists and configure sync settings
            </p>
          </div>

          {/* Step Indicator */}
          <StepIndicator currentStep={currentStep} totalSteps={3} />

          {/* Main Content */}
          <div className="bg-surface rounded-xl border border-border shadow-elevation-1 p-6 lg:p-8">
            {currentStep === 1 && (
              <PlatformSelector
                selectedPlatform={selectedPlatform}
                onPlatformSelect={handlePlatformSelect}
                connectedAccounts={connectedAccounts}
              />
            )}

            {currentStep === 2 && selectedPlatform && (
              <PlaylistGrid
                playlists={
                  playlistsWithTracks.length > 0
                    ? playlistsWithTracks
                    : playlists[selectedPlatform]
                }
                selectedPlaylists={selectedPlaylists}
                onPlaylistToggle={handlePlaylistToggle}
                onSelectAll={handleSelectAll}
                platform={selectedPlatform}
              />
            )}

            {currentStep === 3 && (
              <SyncSummary
                sourcePlatform={selectedPlatform}
                selectedPlaylists={getSelectedPlaylistsData()}
                destinationPlatform={destinationPlatform}
                onContinue={handleContinueToPreview}
                onBack={handleBackToSelection}
              />
            )}
          </div>

          {/* Navigation Footer */}
          {currentStep < 3 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
              <div className="flex items-center space-x-4">
                {currentStep > 1 && (
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back
                  </Button>
                )}
              </div>

              <div className="flex items-center space-x-4">
                {currentStep === 2 && selectedPlaylists.length > 0 && (
                  <div className="text-sm text-text-secondary">
                    {selectedPlaylists.length} playlist
                    {selectedPlaylists.length !== 1 ? "s" : ""} selected
                  </div>
                )}

                <Button
                  variant="primary"
                  onClick={
                    currentStep === 2 ? handleContinueToSummary : undefined
                  }
                  disabled={!canContinue() || isLoading}
                  loading={isLoading}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {currentStep === 2 ? "Review Selection" : "Continue"}
                </Button>
              </div>
            </div>
          )}
        </div>
        
      </div>

      {/* Floating Action Button for Mobile */}
      {currentStep === 2 && selectedPlaylists.length > 0 && (
        <div className="fixed bottom-6 right-6 lg:hidden z-50">
          <Button
            variant="primary"
            onClick={handleContinueToSummary}
            iconName="ArrowRight"
            iconPosition="right"
            className="shadow-elevation-3"
          >
            Continue ({selectedPlaylists.length})
          </Button>
        </div>
      )}
    </div>
  );
};

export default PlaylistSelectionSyncSetup;
